import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Download } from "lucide-react";
import { API, fmtDate } from "./constants";
import { DataTable, Pager } from "./DataTable";

export function OffersTab({ presetStatus, onOpen }) {
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(presetStatus || "");
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page: String(page), limit: "25" });
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    const res = await api.get(`/admin-ops/offers?${params}`);
    setData(res.data);
  }, [page, q, status]);

  useEffect(() => { setStatus(presetStatus || ""); setPage(1); }, [presetStatus]);
  useEffect(() => {
    const t = setTimeout(() => load().catch((e) => setError(formatApiError(e))), 250);
    return () => clearTimeout(t);
  }, [load]);

  const pages = Math.max(1, Math.ceil(data.total / 25));
  return (
    <div className="space-y-3">
      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}
      <div className="flex flex-wrap gap-2">
        <input data-testid="offers-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
          placeholder="Rechercher (titre, employeur, ville, secteur)…"
          className="flex-1 min-w-[220px] text-sm border border-slate-200 rounded-lg px-3.5 py-2.5" />
        <select data-testid="offers-status-filter" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className="text-sm border border-slate-200 rounded-lg px-3 py-2.5 bg-white">
          <option value="">Tous les statuts</option>
          <option value="active">Active</option>
          <option value="publiee">Publiée</option>
          <option value="expired">Expirée</option>
          <option value="brouillon">Brouillon</option>
          <option value="pourvue">Pourvue</option>
          <option value="cloturee">Clôturée</option>
        </select>
        <a data-testid="offers-export" href={`${API}/api/admin-ops/export?type=offers`}
          className="flex items-center gap-1.5 text-xs font-semibold text-brand-navy border border-slate-200 rounded-lg px-3.5 py-2.5 hover:border-brand-turquoise">
          <Download className="w-3.5 h-3.5" /> Exporter CSV
        </a>
      </div>
      <DataTable testid="offers-table" rowKey="offer_id" onRowClick={onOpen}
        empty="Aucune offre ne correspond aux filtres."
        columns={[
          { key: "title", label: "Poste", render: (r) => <span className="font-semibold text-brand-navy">{r.title}</span> },
          { key: "company", label: "Employeur" },
          { key: "city", label: "Lieu", render: (r) => [r.city, r.country].filter(Boolean).join(", ") || "—" },
          { key: "sector", label: "Secteur" },
          { key: "candidates_distinct", label: "Candidats", render: (r) => (
            <span title={`${r.applications_count} candidature(s), ${r.preselected} présélection(s), ${r.interviews} entretien(s), ${r.retained} retenu(s)`}>
              {r.candidates_distinct}
            </span>
          ) },
          { key: "retained", label: "Retenus" },
          { key: "status", label: "Statut", render: (r) => (
            <span className={`text-xs font-medium rounded-full px-2.5 py-1 ${["active", "publiee"].includes(r.status) ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-500"}`}>{r.status}</span>
          ) },
          { key: "created_at", label: "Création", render: (r) => fmtDate(r.created_at) },
        ]}
        rows={data.items} />
      <Pager testid="offers-pager" page={page} pages={pages} total={data.total} onPage={(p) => setPage(Math.min(Math.max(1, p), pages))} />
    </div>
  );
}
